import React, { useContext, useEffect, useState } from 'react';
import { notification, Spin, Modal, Input, Button, Flex } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { LeedflowLogoLight } from '@leedpulse-monorepo/assets';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import SignUpForm from '../sign_up_page/SignUpForm';
import sendPasswordResetEmailUseCase from '../../../domain/use_cases/auth/sendPasswordResetEmail';
import './SignInForm.css';
import { SignupFormProvider } from '../../contexts/forms/SignupFormContext';
import { SIGN_UP_PAGE_PATH } from '../../pages/auth/SignUpPage';
import { useNavigate } from 'react-router-dom';
import { metadata } from '@leedpulse-monorepo/firebase-metadata';

const SignInForm: React.FC = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthenticationContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password) {
      authContext.submitSignIn(email, password);
    }
  };

  const handleForgotPasswordOpen = () => {
    setForgotPasswordEmail(email);
    setShowForgotPassword(true);
  };

  const handleForgotPasswordSubmit = async () => {
    if (!forgotPasswordEmail) {
      notification.error({
        message: 'Erreur',
        description: 'Veuillez entrer votre adresse email.',
      });
      return;
    }

    setIsSendingReset(true);

    const result = await sendPasswordResetEmailUseCase({ email: forgotPasswordEmail });

    if (result.success) {
      notification.success({
        message: 'Email envoyé',
        description: 'Un email de réinitialisation a été envoyé à votre adresse.',
      });
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } else {
      notification.error({
        message: 'Erreur',
        description: result.error || "Une erreur est survenue lors de l'envoi.",
      });
    }

    setIsSendingReset(false);
  };

  useEffect(() => {
    if (authContext.status === ContextStatus.ERROR) {
      notification.error({
        message: 'Erreur',
        description: 'Vérifiez vos informations de connexion.',
      });
    }

    if (authContext.status === ContextStatus.UNAUTHORIZED) {
      notification.error({
        message: 'Erreur',
        description: "Vous n'avez pas accès à cette application.",
      });
    }
  }, [authContext.status]);

  const isLoading = authContext.status === ContextStatus.LOADING;

  if (showSignUp) {
    navigate(SIGN_UP_PAGE_PATH);
  }

  return (
    <div className="signin">
      {/* Decorative arcs - Top Right */}
      <div className="signin__arc signin__arc--tr-1" />
      <div className="signin__arc signin__arc--tr-2" />
      <div className="signin__arc signin__arc--tr-3" />
      <div className="signin__arc signin__arc--tr-4" />

      {/* Decorative arcs - Bottom Left */}
      <div className="signin__arc signin__arc--bl-1" />
      <div className="signin__arc signin__arc--bl-2" />
      <div className="signin__arc signin__arc--bl-3" />
      <div className="signin__arc signin__arc--bl-4" />

      <div className="signin__container">
        <div className="signin__logo">
          <img src={LeedflowLogoLight} alt="LeedFlow" />
        </div>

        <p className="signin__subtitle">Entrez vos identifiants pour accéder à votre compte.</p>

        <form className="signin__form" onSubmit={handleSubmit}>
          <div className="signin__field">
            <label className="signin__label">Email :</label>
            <input
              type="email"
              className="signin__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="signin__field">
            <label className="signin__label">Mot de passe :</label>
            <div className="signin__input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="signin__input signin__input--password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="signin__password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              </button>
            </div>
          </div>

          <Flex vertical gap={8}>
            <Button
              type="text"
              className="signin__forgot-link"
              onClick={handleForgotPasswordOpen}
            >
              Mot de passe oublié ?
            </Button>

            <Button
              type="primary"
              onClick={handleSubmit}
              style={{ width: '100%' }}
              disabled={isLoading}
            >
              {isLoading ? <Spin size="small" /> : 'Se connecter'}
            </Button>

            {metadata('frontendleedflow').databaseId.includes('development') && (
              <Button
                style={{ width: '100%' }}
                onClick={() => setShowSignUp(true)}
                disabled={isLoading}
              >
                Créer un compte
              </Button>
            )}
          </Flex>
        </form>

        <footer className="signin__footer">
          <p className="signin__copyright">Leedflow • Tous droits réservés</p>
          <div className="signin__legal-links">
            <a
              href="https://www.leedflow.com/legal/mentions-legales"
              target="_blank"
              rel="noopener noreferrer"
              className="signin__legal-link"
            >
              Mentions légales
            </a>
            <a
              href="https://www.leedflow.com/legal/politique-de-confidentialite"
              target="_blank"
              rel="noopener noreferrer"
              className="signin__legal-link"
            >
              Politique de confidentialité
            </a>
            <a
              href="https://www.leedflow.com/legal/cgu"
              target="_blank"
              rel="noopener noreferrer"
              className="signin__legal-link"
            >
              Conditions générales d'utilisation
            </a>
          </div>
        </footer>
      </div>

      <Modal
        title="Mot de passe oublié"
        open={showForgotPassword}
        onCancel={() => setShowForgotPassword(false)}
        onOk={handleForgotPasswordSubmit}
        okText="Envoyer"
        cancelText="Annuler"
        confirmLoading={isSendingReset}
      >
        <p style={{ marginBottom: 16 }}>
          Entrez votre adresse email pour recevoir un lien de réinitialisation.
        </p>
        <Input
          type="email"
          placeholder="votre@email.com"
          value={forgotPasswordEmail}
          onChange={(e) => setForgotPasswordEmail(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default SignInForm;
